import { computePercentiles, formatMs } from '@/app/pages/Diagnostics/diagnosticsUtils'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import type { ApiMetric } from '@/stores/diagnosticsStore'
import { useMemo } from 'react'

interface SummaryCardsProps {
	metrics: ApiMetric[]
	totalRequestsSeen: number
	windowLabel: string
}

type Tone = 'default' | 'warning' | 'destructive'

interface CardEntry {
	label: string
	value: string
	hint?: string
	tone?: Tone
}

const toneToBadgeVariant = (tone: Tone): 'default' | 'warning' | 'destructive' => {
	if (tone === 'destructive') {
		return 'destructive'
	}
	if (tone === 'warning') {
		return 'warning'
	}
	return 'default'
}

export const SummaryCards = ({ metrics, totalRequestsSeen, windowLabel }: SummaryCardsProps) => {
	const entries = useMemo<CardEntry[]>(() => {
		const windowCount = metrics.length
		const requestsHint =
			totalRequestsSeen > windowCount ? `${totalRequestsSeen.toLocaleString()} session total` : undefined

		if (windowCount === 0) {
			return [
				{ label: 'Requests', value: '0', hint: requestsHint },
				{ label: 'p50', value: '-' },
				{ label: 'p95', value: '-' },
				{ label: 'p99', value: '-' },
				{ label: 'Error Rate', value: '-' },
			]
		}
		const successDurations: number[] = []
		let errors = 0
		for (const m of metrics) {
			if (m.success) {
				successDurations.push(m.duration)
			} else {
				errors++
			}
		}
		const { p50, p95, p99 } = computePercentiles(successDurations)
		const hasSuccess = successDurations.length > 0
		const errorRate = (errors / windowCount) * 100

		return [
			{ label: 'Requests', value: windowCount.toLocaleString(), hint: requestsHint },
			{ label: 'p50', value: hasSuccess ? formatMs(p50) : '-' },
			{
				label: 'p95',
				value: hasSuccess ? formatMs(p95) : '-',
				tone: hasSuccess && p95 > 1000 ? 'warning' : 'default',
			},
			{
				label: 'p99',
				value: hasSuccess ? formatMs(p99) : '-',
				tone: hasSuccess ? (p99 > 2000 ? 'destructive' : p99 > 1000 ? 'warning' : 'default') : 'default',
			},
			{
				label: 'Error Rate',
				value: `${errorRate.toFixed(1)}%`,
				hint: `${errors} failed`,
				tone: errorRate > 5 ? 'destructive' : errorRate > 1 ? 'warning' : 'default',
			},
		]
	}, [metrics, totalRequestsSeen])

	return (
		<div className='flex flex-col gap-2 px-4'>
			<div className='text-body text-muted-foreground'>Overview · {windowLabel}</div>
			<div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3'>
				{entries.map(entry => (
					<Card key={entry.label} className='px-3 py-2.5 gap-1'>
						<div className='text-body text-muted-foreground'>{entry.label}</div>
						<div className='flex items-center gap-2'>
							{entry.tone && entry.tone !== 'default' ? (
								<Badge variant={toneToBadgeVariant(entry.tone)} className='text-base font-semibold tabular-nums'>
									{entry.value}
								</Badge>
							) : (
								<div className='text-base font-semibold tabular-nums'>{entry.value}</div>
							)}
						</div>
						{entry.hint ? <div className='text-body text-muted-foreground'>{entry.hint}</div> : null}
					</Card>
				))}
			</div>
		</div>
	)
}
